import { Alert, Grid2 as Grid, MenuItem, TextField, Typography } from "@mui/material";
import { TariffSelect } from "@/components/TariffSelect";
import type { RateDerivation } from "@/api/endpoints";

/** The three fields that together say how a price row arrives at its rate (W13). */
export interface DerivationValue {
  derivation: RateDerivation;
  /** "" when the basis needs no base card. */
  derivedFromTariffId: string;
  /** Kept as text so a half-typed "−" or "-1" doesn't jump to a number mid-keystroke. */
  derivationAdjustmentPct: string;
}

export const absoluteDerivation = (): DerivationValue => ({
  derivation: "Absolute", derivedFromTariffId: "", derivationAdjustmentPct: "0",
});

/**
 * Reads the three fields back off a saved row. A row that is not derived comes back with the
 * adjustment blanked rather than "0", so switching the basis on presents an empty box to type in
 * rather than a zero to delete first.
 */
export const derivationFrom = (row: {
  derivation?: RateDerivation | null;
  derivedFromTariffId?: string | null;
  derivationAdjustmentPct?: number | null;
}): DerivationValue => ({
  derivation: row.derivation ?? "Absolute",
  derivedFromTariffId: row.derivedFromTariffId ?? "",
  derivationAdjustmentPct:
    row.derivation && row.derivation !== "Absolute" ? String(row.derivationAdjustmentPct ?? 0) : "0",
});

/** What the API wants. Fields the chosen basis has no use for are dropped, not sent as blanks. */
export const derivationPayload = (v: DerivationValue) => ({
  derivation: v.derivation,
  derivedFromTariffId: v.derivation === "BaseTariff" ? v.derivedFromTariffId || null : null,
  derivationAdjustmentPct: v.derivation === "Absolute" ? 0 : Number(v.derivationAdjustmentPct || 0),
});

/**
 * Whatever stops this being saveable, in the words the maintainer needs — null when it is fine.
 * Mirrors the server's rules so the dialog can refuse before the round trip.
 */
export function derivationError(v: DerivationValue, allowMrp: boolean): string | null {
  if (v.derivation === "Absolute") return null;
  if (v.derivation === "Mrp" && !allowMrp) {
    return "A service has no MRP to derive from — that basis is for stock items.";
  }
  if (v.derivation === "BaseTariff" && !v.derivedFromTariffId) {
    return "Pick the rate card this rate is measured against.";
  }
  const pct = Number(v.derivationAdjustmentPct);
  if (v.derivationAdjustmentPct === "" || Number.isNaN(pct)) return "Enter an adjustment percentage.";
  if (pct < -100) return "An adjustment below −100% would go negative.";
  if (pct > 1000) return "An adjustment above +1000% is almost certainly a typo.";
  if (v.derivation === "Mrp" && pct > 0) {
    return "MRP plus a markup would price above the printed MRP, which cannot be billed.";
  }
  return null;
}

/** "less 10%" / "plus 15%" / "at cost" — the phrase a contract would use. */
export function adjustmentPhrase(pct: number): string {
  if (pct === 0) return "at the same amount";
  const size = Math.abs(pct).toLocaleString(undefined, { maximumFractionDigits: 4 });
  return pct < 0 ? `less ${size}%` : `plus ${size}%`;
}

/**
 * How a price row arrives at its rate (W13).
 *
 * <p>Payer agreements are written as differences — "CGHS is the published card less ten percent",
 * "corporate is MRP less fifteen". Typed as two thousand absolute amounts, all two thousand have
 * to be re-typed when the base moves, and any that gets missed is silently wrong. These fields let
 * the row state the difference and leave the amount to be worked out at billing time.</p>
 *
 * <p>The preview line is the point of the control. A percentage off a card the maintainer cannot
 * see the contents of is abstract; showing what a round number would come to makes a sign error
 * obvious while the dialog is still open.</p>
 */
export function DerivationFields({
  value, onChange, allowMrp, ownTariffId, disabled, previewBase = 1000,
}: {
  value: DerivationValue;
  onChange: (v: DerivationValue) => void;
  /** Stock items carry an MRP to derive from; services do not. */
  allowMrp: boolean;
  /** The card this row is on — it cannot be its own base. */
  ownTariffId?: string | null;
  disabled?: boolean;
  /** The round number the preview sentence measures against. */
  previewBase?: number;
}) {
  const set = <K extends keyof DerivationValue>(k: K, v: DerivationValue[K]) =>
    onChange({ ...value, [k]: v });

  const derived = value.derivation !== "Absolute";
  const pct = Number(value.derivationAdjustmentPct || 0);
  const error = derivationError(value, allowMrp);
  const previewOk = !error && Number.isFinite(pct);
  const preview = Math.max(0, Math.round(previewBase * (1 + pct / 100) * 100) / 100);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: derived ? 4 : 12 }}>
        <TextField select size="small" fullWidth label="Priced as" disabled={disabled}
          value={value.derivation}
          onChange={(e) => {
            const next = e.target.value as RateDerivation;
            onChange({
              derivation: next,
              // Switching away from a base card drops it rather than keeping it around to
              // reappear if somebody switches back.
              derivedFromTariffId: next === "BaseTariff" ? value.derivedFromTariffId : "",
              derivationAdjustmentPct: next === "Absolute" ? "0" : value.derivationAdjustmentPct,
            });
          }}
          helperText={derived ? undefined : "A typed amount, charged as entered"}>
          <MenuItem value="Absolute">A fixed amount</MenuItem>
          <MenuItem value="BaseTariff">% of another rate card</MenuItem>
          {allowMrp && <MenuItem value="Mrp">% of the printed MRP</MenuItem>}
        </TextField>
      </Grid>

      {derived && (
        <>
          <Grid size={{ xs: 12, sm: 5 }}>
            {value.derivation === "BaseTariff" ? (
              <TariffSelect
                label="Base rate card *"
                value={value.derivedFromTariffId}
                onChange={(v) => set("derivedFromTariffId", v)}
                disabled={disabled}
                excludeId={ownTariffId}
                includeSuperseded
                blankLabel="— Pick a card —"
                error={!value.derivedFromTariffId}
                helperText="The card this rate is measured against"
              />
            ) : (
              <TextField size="small" fullWidth disabled label="Base"
                value="Highest MRP the item currently carries"
                helperText="Across live batches, falling back to the item's default" />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField type="number" size="small" fullWidth label="Adjustment %" disabled={disabled}
              value={value.derivationAdjustmentPct}
              onChange={(e) => set("derivationAdjustmentPct", e.target.value)}
              inputProps={{ step: "0.01", min: -100, max: 1000 }}
              helperText="Signed: −10 is ten percent off" />
          </Grid>
        </>
      )}

      {derived && (
        <Grid size={12}>
          {error ? (
            <Alert severity="warning">{error}</Alert>
          ) : (
            <Alert severity="info">
              Charged at the base {adjustmentPhrase(pct)}, worked out on every bill.
              {previewOk && (
                <Typography component="span" variant="body2" sx={{ display: "block", mt: 0.5 }}>
                  A base of {previewBase.toFixed(2)} would bill <b>{preview.toFixed(2)}</b>. The
                  amount follows the base whenever it moves — nothing here is re-typed.
                </Typography>
              )}
            </Alert>
          )}
        </Grid>
      )}
    </Grid>
  );
}

/** Compact "CGHS-2026 less 10%" for a grid cell. Null when the row carries a typed amount. */
export function derivationLabel(row: {
  derivation?: RateDerivation | null;
  derivedFromTariffCode?: string | null;
  derivationAdjustmentPct?: number | null;
}): string | null {
  if (!row.derivation || row.derivation === "Absolute") return null;
  const base = row.derivation === "Mrp" ? "MRP" : row.derivedFromTariffCode ?? "base card";
  return `${base} ${adjustmentPhrase(row.derivationAdjustmentPct ?? 0)}`;
}
